import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

const ITEMS_PER_PAGE = 10;

export const useAnalysisHistory = (userId) => {
    const [analyses, setAnalyses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [sortConfig, setSortConfig] = useState({ column: 'timestamp', direction: 'desc' });
    const [filters, setFilters] = useState({
        status: '',
        search: '',
        analysisType: '',
    });

    const fetchAnalyses = async () => {
        if (!userId) return;

        setLoading(true);
        setError(null);

        try {
            let query = supabase
                .from('analyses_history')
                .select('*', { count: 'exact' })
                .eq('user_id', userId);

            // Apply filters
            if (filters.status && filters.status !== 'all') {
                query = query.eq('status', filters.status);
            }
            if (filters.search) {
                query = query.or(
                    `analysis_name.ilike.%${filters.search}%,` +
                    `primary_analysis_type_name.ilike.%${filters.search}%,` +
                    `secondary_analysis_type_name.ilike.%${filters.search}%`
                );
            }
            if (filters.analysisType) {
                query = query.or(`primary_analysis_type.eq.${filters.analysisType},secondary_analysis_type.eq.${filters.analysisType}`);
            }

            // Apply sorting
            query = query.order(sortConfig.column, { ascending: sortConfig.direction === 'asc' });

            // Apply pagination
            const start = (currentPage - 1) * ITEMS_PER_PAGE;
            query = query.range(start, start + ITEMS_PER_PAGE - 1);

            const { data, count, error } = await query;

            if (error) throw error;

            setAnalyses(data || []);
            setTotalPages(Math.ceil((count || 0) / ITEMS_PER_PAGE));
        } catch (err) {
            console.error('Error fetching analyses:', err);
            setError('Failed to load analysis history');
        } finally {
            setLoading(false);
        }
    };

    // Set up real-time subscription
    useEffect(() => {
        if (!userId) return;

        // Subscribe to changes in analyses_history table for the current user
        const channel = supabase
            .channel(`analyses_history_${userId}`)
            .on('postgres_changes', {
                event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
                schema: 'public',
                table: 'analyses_history',
                filter: `user_id=eq.${userId}` // Only listen to changes for the current user
            }, (payload) => {
                console.log('Received real-time update:', payload);

                // Handle different types of changes
                switch (payload.eventType) {
                    case 'INSERT':
                        // If we're on the first page and sorting by timestamp desc (newest first)
                        if (currentPage === 1 && sortConfig.column === 'timestamp' && sortConfig.direction === 'desc') {
                            fetchAnalyses(); // Refetch to ensure correct ordering
                        }
                        break;

                    case 'UPDATE':
                        // Update the analysis in place if it's in the current view
                        setAnalyses(prevAnalyses => {
                            const updatedAnalyses = prevAnalyses.map(analysis =>
                                analysis.id === payload.new.id ? payload.new : analysis
                            );
                            return updatedAnalyses;
                        });
                        break;

                    case 'DELETE':
                        // Remove the analysis if it's in the current view
                        setAnalyses(prevAnalyses => {
                            const filteredAnalyses = prevAnalyses.filter(analysis =>
                                analysis.id !== payload.old.id
                            );
                            // If we removed an item and the list is now empty, refetch to get next page
                            if (filteredAnalyses.length === 0 && prevAnalyses.length > 0) {
                                fetchAnalyses();
                                return prevAnalyses; // Keep current state until fetch completes
                            }
                            return filteredAnalyses;
                        });
                        break;
                }
            })
            .subscribe((status) => {
                console.log('Subscription status:', status);
            });

        // Cleanup subscription on unmount or userId change
        return () => {
            channel.unsubscribe();
        };
    }, [userId, currentPage, sortConfig]); // Dependencies include pagination and sorting state

    // Fetch analyses when parameters change
    useEffect(() => {
        fetchAnalyses();
    }, [userId, currentPage, sortConfig, filters]);

    return {
        analyses,
        loading,
        error,
        currentPage,
        totalPages,
        sortConfig,
        filters,
        setCurrentPage,
        setSortConfig,
        setFilters,
        refetch: fetchAnalyses,
    };
};